import axios from 'axios';

const baseUrl = 'https://api.spygram.info';
axios.defaults.withCredentials = true;

// Настройка перехватчика ошибок
axios.interceptors.response.use(
  response => response, // Пропускаем успешные ответы
  error => {
    if (error.response?.status === 401) {
      // Возвращаем пустой ответ и не выводим ошибку в консоль
      return Promise.resolve({ data: { success: false, message: 'Unauthorized' } });
    }

    // Для остальных ошибок, передаем их дальше, без вывода в консоль
    return Promise.reject(error);
  }
);

/**
 * Обновление информации о пользователе (например, имени).
 * @param {string} name - Новое имя пользователя.
 * @returns {Object} - Результат обновления.
 * @throws {Error} - Ошибка при обновлении информации.
 */
export const updateUserInfo = async name => {
  try {
    const response = await axios.post(`${baseUrl}/common/update-user-info`, { name });
    return handleResponse(response);
  } catch (error) {
    if (error.response?.data?.data?.name) {
      // Если есть ошибки валидации имени, выбрасываем их как сообщение об ошибке
      throw new Error(error.response.data.data.name.join(', '));
    }
    console.error('Ошибка при обновлении информации:', error);
    throw new Error(error.response?.data?.message || 'Ошибка при обновлении информации');
  }
};

/**
 * Вспомогательная функция для обработки ответа сервера.
 * @param {Object} response - Ответ сервера.
 * @returns {Object} - Данные ответа.
 * @throws {Error} - Ошибка, если ответ не успешный.
 */
const handleResponse = response => {
  if (!response.data.success) {
    throw new Error(response.data.message);
  }
  return response.data;
};

/**
 * Авторизация пользователя.
 * @param {string} email - Email пользователя.
 * @param {string} password - Пароль пользователя.
 * @returns {Object} - Ответ от сервера.
 */
export const login = async (email, password) => {
  const response = await axios.post(`${baseUrl}/auth/login`, { email, password });
  return response.data; // Возвращаем данные ответа
};
/**
 * Выход из аккаунта.
 * @returns {Object} - Результат выхода.
 * @throws {Error} - Ошибка выхода.
 */
export const logout = async () => {
  try {
    const response = await axios.post(`${baseUrl}/auth/logout`);
    return handleResponse(response);
  } catch (error) {
    console.error('Ошибка выхода:', error);
    throw new Error(error.response?.data?.message || 'Ошибка выхода');
  }
};
/**
 * Получение текущего пользователя.
 * @returns {Object|null} - Данные пользователя или null, если пользователь не авторизован.
 */
export const getUser = async () => {
  try {
    const response = await axios.get(`${baseUrl}/common/get-user`);
    return response.data.success ? response.data.data : null;
  } catch (error) {
    // console.error('Ошибка при получении пользователя:', error); // Закомментируйте или удалите эту строку
    return null;
  }
};

/**
 * Получение ID пользователя по имени.
 * @param {string} username - Имя пользователя.
 * @returns {Object} - ID пользователя.
 * @throws {Error} - Ошибка при получении ID.
 */
export const getProfileId = async username => {
  try {
    const response = await axios.get(`${baseUrl}/utils/get-id-by-resource`, {
      params: { resource: username }
    });
    return handleResponse(response).data;
  } catch (error) {
    // console.error('Ошибка при получении ID:', error); // Логирование ошибки
    throw new Error(error.response?.data?.message || 'Ошибка при получении ID');
  }
};

/**
 * Бесплатный анализ профиля.
 * @param {string} profileId - ID профиля
 * @returns {Object} - Результат анализа.
 */
export const analyzeProfile = async profileId => {
  try {
    const analyzeVkUserId = Number(profileId);

    if (isNaN(analyzeVkUserId)) {
      return { success: false, message: 'Некорректный ID профиля' };
    }

    const response = await axios.post(`${baseUrl}/analyze/free-analyze`, {
      analyzeVkUserId
    });

    // Проверяем статус ответа
    if (response.status !== 200) {
      return { success: false, message: `Неожиданный статус ответа: ${response.status}` };
    }

    // Возвращаем ответ от сервера, даже если success: false
    return response.data;
  } catch (error) {
    // Если ошибка связана с авторизацией, возвращаем pending
    if (error.response?.status === 401) {
      return { success: true, data: { status: 'pending' } };
    }

    // Возвращаем сообщение об ошибке от сервера
    return {
      success: false,
      message: error.response?.data?.message || 'Ошибка при анализе профиля'
    };
  }
};

export const fullAnalyze = async profileId => {
  try {
    // Запускаем анализ
    const startResponse = await axios.post(`${baseUrl}/analyze/full-analyze`, {
      analyzeVkUserId: profileId
    });

    if (!startResponse.data.success) {
      // Если анализ уже в процессе, не выбрасываем ошибку
      if (startResponse.data.message === 'Анализ уже в процессе') {
        return await pollAnalysisStatus(profileId); // Ждем завершения анализа
      }
      throw new Error(startResponse.data.message || 'Ошибка запуска анализа');
    }

    // Ждем завершения анализа
    return await pollAnalysisStatus(profileId);
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Ошибка при полном анализе профиля');
  }
};

/**
 * Получение сессии анализа.
 * @param {string} profileId - ID профиля.
 * @returns {Object} - Данные сессии анализа.
 * @throws {Error} - Ошибка при получении сессии анализа.
 */
export const getSession = async profileId => {
  try {
    const response = await axios.post(`${baseUrl}/analyze/get-session`, {
      analyzeVkUserId: profileId
    });

    return {
      success: response.data.success,
      message: response.data.message,
      data: response.data.data
    };
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Ошибка при получении сессии анализа');
  }
};

/**
 * Функция для опроса статуса анализа
 * @param {string} profileId - ID профиля
 * @returns {Promise<Object>} - Результат анализа
 */
export const pollAnalysisStatus = async profileId => {
  const maxAttempts = 200;
  const pollInterval = 2000; // 2 секунды
  let attempts = 0;

  while (attempts < maxAttempts) {
    try {
      const sessionResponse = await getSession(profileId);

      if (sessionResponse.success && sessionResponse.data.status === 'completed') {
        return sessionResponse;
      }

      attempts++;
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    } catch (error) {
      throw error;
    }
  }

  throw new Error('Превышено время ожидания анализа');
};

/**
 * Получение платежной ссылки.
 * @returns {string} - Ссылка на оплату.
 * @throws {Error} - Ошибка при получении ссылки на оплату.
 */
export const getPaymentUrl = async () => {
  try {
    const response = await axios.get(`${baseUrl}/common/get-pay-url`);
    return handleResponse(response).data;
  } catch (error) {
    console.error('Ошибка при получении ссылки на оплату:', error);
    throw new Error(error.response?.data?.message || 'Ошибка при получении ссылки на оплату');
  }
};

/**
 * Объединённая функция: получение ID + запуск анализа.
 * @param {string} username - Имя пользователя.
 * @param {boolean} isAuthorized - Флаг авторизации пользователя.
 * @returns {Object} - Результат анализа.
 * @throws {Error} - Ошибка при анализе профиля.
 */
export const getProfileDataAndAnalyze = async (username, isAuthorized) => {
  try {
    const profileId = await getProfileId(username);
    console.log('Полученный ID:', profileId);

    let analysisResult;
    if (isAuthorized) {
      analysisResult = await fullAnalyze(profileId);
    } else {
      analysisResult = await analyzeProfile(profileId);
    }

    console.log('Результат анализа:', analysisResult);
    return analysisResult;
  } catch (error) {
    if (error.message.includes('Нет возможности анализировать закрытые профили')) {
      console.error('Профиль закрыт для анализа');
      throw new Error('Профиль закрыт для анализа');
    } else {
      console.error('Ошибка при анализе профиля:', error.message);
      throw new Error('Ошибка при анализе профиля');
    }
  }
};

/**
 * Получение истории анализов.
 * @returns {Object} - История анализов, отсортированная по времени.
 * @throws {Error} - Ошибка при получении истории анализов.
 */
export const getHistory = async () => {
  try {
    const response = await axios.get(`${baseUrl}/analyze/get-history`);
    const data = handleResponse(response).data;

    // Сортируем данные по времени (от новых к старым)
    data.sort((a, b) => b.time - a.time);

    return data;
  } catch (error) {
    console.error('Ошибка при получении истории анализов:', error);
    throw new Error(error.response?.data?.message || 'Ошибка при получении истории анализов');
  }
};
